import { useCallback, useEffect, useRef, useState } from 'react';

const getTextBlock = node => {
  const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  return element?.closest('[data-content-block]') || null;
};

export function useTagging(editableRef) {
  const [isOpen, setIsOpen] = useState(false);
  const activeTriggerRef = useRef(null);

  const close = useCallback(() => {
    activeTriggerRef.current = null;
    setIsOpen(false);
  }, []);

  const replaceActiveTrigger = useCallback(tag => {
    const activeTrigger = activeTriggerRef.current;
    const editor = editableRef.current;
    const username = tag?.user_username;

    if (!activeTrigger || !editor || !username) return null;

    const { blockKey, range } = activeTrigger;
    if (!editor.contains(range.startContainer) || !editor.contains(range.endContainer)) {
      close();
      return null;
    }

    const block = getTextBlock(range.startContainer);
    if (!block || block.dataset.contentBlock !== blockKey) {
      close();
      return null;
    }

    const mention = document.createElement('span');
    mention.className = 'tag-display';
    mention.contentEditable = 'false';
    mention.textContent = `@${username}`;
    range.deleteContents();
    range.insertNode(mention);

    const nextNode = mention.nextSibling;
    const needsTrailingSpace = !(
      nextNode?.nodeType === Node.TEXT_NODE && /^\s/.test(nextNode.nodeValue || '')
    );
    const caretNode = needsTrailingSpace ? document.createTextNode(' ') : mention;

    if (needsTrailingSpace) mention.after(caretNode);

    const selection = window.getSelection();
    const caretRange = document.createRange();
    caretRange.setStartAfter(caretNode);
    caretRange.collapse(true);
    block.focus();
    selection?.removeAllRanges();
    selection?.addRange(caretRange);

    activeTriggerRef.current = null;
    setIsOpen(false);

    return { blockKey, content: block.innerHTML };
  }, [close, editableRef]);

  useEffect(() => {
    const editor = editableRef.current;
    if (!editor) return undefined;

    const updateTrigger = () => {
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      const textNode = range?.startContainer;

      if (
        !range?.collapsed
        || !textNode
        || textNode.nodeType !== Node.TEXT_NODE
        || !editor.contains(textNode)
      ) {
        close();
        return;
      }

      const textBeforeCaret = textNode.nodeValue.slice(0, range.startOffset);
      const markerIndex = textBeforeCaret.lastIndexOf('@');
      const query = markerIndex >= 0 ? textBeforeCaret.slice(markerIndex + 1) : '';
      const block = getTextBlock(textNode);

      if (markerIndex < 0 || /\s/.test(query) || !block?.dataset.contentBlock) {
        close();
        return;
      }

      const triggerRange = document.createRange();
      triggerRange.setStart(textNode, markerIndex);
      triggerRange.setEnd(textNode, range.startOffset);
      activeTriggerRef.current = {
        blockKey: block.dataset.contentBlock,
        range: triggerRange,
      };
      setIsOpen(true);
    };

    editor.addEventListener('keyup', updateTrigger);
    return () => editor.removeEventListener('keyup', updateTrigger);
  }, [close, editableRef]);

  return { isOpen, close, replaceActiveTrigger };
}
